import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { ChatMessage, GradingSession } from '../grading.types';

/**
 * Centralized state management service for grading chat.
 * Provides reactive state for session, loading states, and errors.
 */
@Injectable()
export class GradingStateService {
  // Session state
  private readonly session$$ = new BehaviorSubject<GradingSession | null>(null);
  readonly session$ = this.session$$.asObservable();

  // Messages derived from session
  readonly messages$: Observable<ChatMessage[]> = this.session$.pipe(
    map((session) => session?.messages ?? [])
  );

  // Loading state for initial session load/create
  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this.isLoading$$.asObservable();

  // Loading state for sending a message (waiting for AI response)
  private readonly isSending$$ = new BehaviorSubject<boolean>(false);
  readonly isSending$ = this.isSending$$.asObservable();

  // Error state
  private readonly error$$ = new BehaviorSubject<string | null>(null);
  readonly error$ = this.error$$.asObservable();

  /**
   * Get current session ID
   */
  get sessionId(): string | null {
    return this.session$$.value?.id ?? null;
  }

  /**
   * Get current session value
   */
  get session(): GradingSession | null {
    return this.session$$.value;
  }

  /**
   * Update session state
   */
  setSession(session: GradingSession | null): void {
    this.session$$.next(session);
  }

  /**
   * Update loading state
   */
  setLoading(isLoading: boolean): void {
    this.isLoading$$.next(isLoading);
  }

  /**
   * Update sending state
   */
  setSending(isSending: boolean): void {
    this.isSending$$.next(isSending);
  }

  /**
   * Update error state
   */
  setError(error: string | null): void {
    this.error$$.next(error);
  }

  /**
   * Update messages in current session
   */
  updateMessages(messages: ChatMessage[]): void {
    const currentSession = this.session$$.value;
    if (currentSession) {
      this.session$$.next({
        ...currentSession,
        messages,
      });
    }
  }

  /**
   * Add a message to current session
   */
  addMessage(message: ChatMessage): void {
    const currentSession = this.session$$.value;
    if (currentSession) {
      this.session$$.next({
        ...currentSession,
        messages: [...currentSession.messages, message],
      });
    }
  }

  /**
   * Update or add a message in current session
   * If message with same ID exists, it will be updated; otherwise, added
   */
  upsertMessage(message: ChatMessage): void {
    const currentSession = this.session$$.value;
    if (!currentSession) return;

    const existingIndex = currentSession.messages.findIndex(
      (m) => m.id === message.id
    );

    let updatedMessages: ChatMessage[];
    if (existingIndex >= 0) {
      updatedMessages = [...currentSession.messages];
      updatedMessages[existingIndex] = message;
    } else {
      updatedMessages = [...currentSession.messages, message];
    }

    this.session$$.next({
      ...currentSession,
      messages: updatedMessages,
    });
  }

  /**
   * Clear all state
   */
  clearState(): void {
    this.session$$.next(null);
    this.error$$.next(null);
  }
}
